<template>
	<div class="content">
		<div class="section">
			<div class="section-tit">제휴 수신함</div>

			<!-- 필터 -->
			<div style="display: flex; gap: 8px; align-items: center; margin-bottom: 12px; flex-wrap: wrap">
				<select class="form-control" style="width: auto" v-model="filterStatus" @change="reload">
					<option value="">전체 상태</option>
					<option value="new">신규</option>
					<option value="reading">확인중</option>
					<option value="done">완료</option>
				</select>
				<select class="form-control" style="width: auto" v-model="filterInstitution" @change="reload">
					<option value="">전체 기관</option>
					<option v-for="i in institutions" :key="i.code" :value="i.code">{{ i.label }}</option>
				</select>
				<button type="button" class="btn btn-sm btn-outline-secondary" @click="reload">새로고침</button>
				<span style="color: #888">총 {{ totalCount }}건</span>
			</div>

			<!-- 목록 -->
			<div class="table-wrap">
				<table class="table">
					<colgroup>
						<col width="7%" />
						<col width="12%" />
						<col width="8%" />
						<col width="6%" />
						<col width="13%" />
						<col width="15%" />
						<col width="27%" />
						<col width="12%" />
					</colgroup>
					<tr>
						<th>no</th>
						<th>기관</th>
						<th>상태</th>
						<th>건수</th>
						<th>담당자</th>
						<th>접수일시</th>
						<th>메모</th>
						<th></th>
					</tr>
					<tr v-if="!list.length">
						<td colspan="8" style="text-align: center; color: #888; padding: 30px 0">수신된 요청이 없습니다.</td>
					</tr>
					<tr
						v-for="req in list"
						:key="req.request_uid"
						:class="{ 'row-active': detail && detail.request.request_uid === req.request_uid }"
					>
						<td>{{ req.request_uid }}</td>
						<td>{{ institutionLabel(req.institution) }}</td>
						<td>
							<span class="badge" :class="statusClass(req.status)">{{ statusLabel(req.status) }}</span>
						</td>
						<td>{{ req.item_count }}</td>
						<td>{{ assigneeText(req) }}</td>
						<td>{{ fmtDate(req.created_at) }}</td>
						<td style="text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 0">
							{{ req.memo }}
						</td>
						<td>
							<button type="button" class="btn btn-sm btn-outline-primary" @click="openDetail(req.request_uid)">상세</button>
						</td>
					</tr>
				</table>
			</div>

			<!-- 페이징 -->
			<div
				v-if="totalPages > 1"
				style="display: flex; gap: 8px; justify-content: center; align-items: center; margin-top: 12px"
			>
				<button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page <= 1" @click="goPage(page - 1)">
					이전
				</button>
				<span>{{ page }} / {{ totalPages }}</span>
				<button
					type="button"
					class="btn btn-sm btn-outline-secondary"
					:disabled="page >= totalPages"
					@click="goPage(page + 1)"
				>
					다음
				</button>
			</div>
		</div>

		<!-- 상세 -->
		<div class="section" v-if="detail">
			<div class="section-tit">
				요청 #{{ detail.request.request_uid }} 상세
				<button type="button" class="btn btn-sm btn-outline-secondary" style="float: right" @click="detail = null">
					닫기
				</button>
			</div>

			<div style="display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 12px">
				<div><b>기관</b> {{ institutionLabel(detail.request.institution) }}</div>
				<div><b>회원UID</b> {{ detail.request.mem_uid }}</div>
				<div><b>건수</b> {{ detail.request.item_count }}</div>
				<div><b>접수</b> {{ fmtDate(detail.request.created_at) }}</div>
				<div><b>담당자</b> {{ assigneeText(detail.request) }}</div>
			</div>

			<div v-if="detail.request.memo" style="margin-bottom: 12px">
				<b>요청 메모</b>
				<div style="white-space: pre-wrap; background: #f7f7f7; padding: 8px; border-radius: 4px">{{ detail.request.memo }}</div>
			</div>

			<!-- 상태 변경 -->
			<div style="display: flex; gap: 8px; align-items: center; margin-bottom: 16px; flex-wrap: wrap">
				<select class="form-control" style="width: auto" v-model="editStatus">
					<option value="new">신규</option>
					<option value="reading">확인중</option>
					<option value="done">완료</option>
				</select>
				<label style="display: flex; gap: 4px; align-items: center; margin: 0">
					<input type="checkbox" v-model="assignSelf" /> 나에게 배정
				</label>
				<button type="button" class="btn btn-sm btn-primary" @click="saveStatus" :disabled="saving">상태 저장</button>
			</div>

			<!-- 스냅샷 항목 -->
			<div class="table-wrap">
				<table class="table">
					<colgroup>
						<col width="8%" />
						<col width="10%" />
						<col width="32%" />
						<col width="50%" />
					</colgroup>
					<tr>
						<th>no</th>
						<th>구분</th>
						<th>물건명</th>
						<th>주소</th>
					</tr>
					<tr v-for="(it, idx) in detail.items" :key="it.item_uid">
						<td>{{ idx + 1 }}</td>
						<td>{{ it.building_type === 'rent' ? '임대' : '매매' }}</td>
						<td>{{ it.snap_building_name }}</td>
						<td>{{ it.snap_road_addr || it.snap_jibun_addr }}</td>
					</tr>
				</table>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'PartnerInbox',
	data() {
		return {
			institutions: [],
			list: [],
			totalCount: 0,
			page: 1,
			size: 20,
			totalPages: 1,
			filterStatus: '',
			filterInstitution: '',
			detail: null,
			editStatus: 'new',
			assignSelf: false,
			saving: false,
		};
	},
	created() {
		this.loadInstitutions();
		this.getList();
	},
	methods: {
		async loadInstitutions() {
			const re = await this.$apiGET('/partner/api/institutions');
			if (re && re.st && Array.isArray(re.list)) this.institutions = re.list;
		},
		institutionLabel(code) {
			const found = this.institutions.find(i => i.code === code);
			return found ? found.label : code;
		},
		statusLabel(s) {
			return { new: '신규', reading: '확인중', done: '완료' }[s] || s;
		},
		statusClass(s) {
			return { new: 'bg-danger', reading: 'bg-warning', done: 'bg-secondary' }[s] || 'bg-secondary';
		},
		fmtDate(v) {
			if (!v) return '';
			return String(v).replace('T', ' ').slice(0, 16);
		},
		// 자동 배정된 담당자(매물 담당자=위온어스 직원). 미배정이면 '미지정'.
		assigneeText(req) {
			if (!req || !req.assignee_uid) return '미지정';
			const name = req.assignee_name || '#' + req.assignee_uid;
			return req.assignee_rank_name ? name + ' ' + req.assignee_rank_name : name;
		},
		async getList() {
			const re = await this.$apiGET(
				'/partner/api/inbox?status=' +
					this.filterStatus +
					'&institution=' +
					this.filterInstitution +
					'&page=' +
					this.page +
					'&size=' +
					this.size
			);
			if (re && re.st) {
				this.list = Array.isArray(re.list) ? re.list : [];
				this.totalCount = re.totalCount || 0;
				this.totalPages = re.totalPages || 1;
				this.page = re.page || 1;
			}
		},
		reload() {
			this.page = 1;
			this.getList();
		},
		goPage(p) {
			if (p < 1 || p > this.totalPages) return;
			this.page = p;
			this.getList();
		},
		async openDetail(id) {
			const re = await this.$apiGET('/partner/api/inbox/detail?id=' + id);
			if (re && re.st) {
				this.detail = { request: re.request, items: Array.isArray(re.items) ? re.items : [] };
				this.editStatus = re.request.status;
				this.assignSelf = false;
			}
		},
		async saveStatus() {
			if (!this.detail) return;
			this.saving = true;
			const param = { id: this.detail.request.request_uid, status: this.editStatus };
			if (this.assignSelf) param.assigneeUid = this.$store.getters.getUserInfo.mem_uid;
			const re = await this.$apiPOST('/partner/api/inbox/status', param);
			this.saving = false;
			if (re && re.st) {
				alert('저장되었습니다.');
				await this.openDetail(this.detail.request.request_uid);
				this.getList();
			}
		},
	},
};
</script>

<style scoped>
.row-active {
	background: #eef4ff;
}
</style>
